<template>
  <router-link
    v-if="route"
    :to="route"
    class="adn-button"
    :class="classNames"
    data-test-button
    :disabled="disabled ? 'disabled' : undefined"
    @click="onClick"
  >
    <span
      v-if="iconName && !iconsPositionIsRight"
      :class="iconClassNames"
      class="adn-button__icon adn-button__icon--left"
      data-test-icon
      aria-hidden="true"
    />
    <span v-if="hasContent()" class="adn-button__text" data-test-text>
      <!-- @slot Use this slot to set the button label -->
      <slot></slot>
    </span>
    <span
      v-if="iconName && iconsPositionIsRight"
      :class="iconClassNames"
      class="adn-button__icon adn-button__icon--right"
      data-test-icon
      aria-hidden="true"
    />
  </router-link>
  <component
    :is="markup"
    v-else
    class="adn-button"
    :class="classNames"
    role="button"
    data-test-button
    :disabled="disabled ? 'disabled' : undefined"
    @click="onClick"
  >
    <span
      v-if="iconName && !iconsPositionIsRight"
      :class="iconClassNames"
      class="adn-button__icon adn-button__icon--left"
      data-test-icon
      aria-hidden="true"
    />
    <span v-if="hasContent()" class="adn-button__text" data-test-text>
      <!-- @slot Use this slot to set the button label -->
      <slot></slot>
    </span>
    <span
      v-if="iconName && iconsPositionIsRight"
      :class="iconClassNames"
      class="adn-button__icon adn-button__icon--right"
      data-test-icon
      aria-hidden="true"
    />
  </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'AdnButton',
  props: {
    kind: {
      type: String,
      default: 'primary',
      validator: (val: string) => ['primary', 'secondary', 'link', 'tertiary', 'light'].includes(val),
    },
    iconName: {
      type: String,
      default: '',
    },
    iconPosition: {
      type: String,
      default: 'right',
      validator: (val: string) => ['right', 'left'].includes(val),
    },
    markup: {
      type: String,
      default: 'button',
      validator: (val: string) => ['button', 'router-link', 'div', 'span', 'a'].includes(val),
    },
    color: {
      type: String,
      default: 'default',
      validator: (val: string) => ['default', 'white'].includes(val),
    },
    route: {
      type: [String, Object],
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    classNames() {
      const classNames = [
        `adn-button--${this.kind.toLowerCase()}`,
        `adn-button--${this.kind.toLowerCase()}-${this.color.toLowerCase()}`,
      ]
      return classNames
    },
    iconClassNames() {
      const iconClassNames = ['adn-icon', `adn-icon--${this.iconName.toLowerCase()}`]
      return iconClassNames
    },
    iconsPositionIsRight() {
      return this.iconPosition === 'right'
    },
  },
  methods: {
    onClick(event: unknown) {
      return this.$emit('click', event)
    },
    hasContent() {
      return !!this.$slots.default
    },
  },
})
</script>

<style lang="scss">
.adn-button {
  cursor: pointer;
  border: solid 1px transparent;
  border-radius: var(--border-radius-s);
  color: var(--color-main);
  min-height: 4.4rem;
  display: inline-block;
  font-size: var(--font-size-m);
  font-weight: bold;
  line-height: 4.2rem;
  padding: 0 2rem;
  transition: all 0.1s ease-in;
  box-sizing: border-box;
  outline: none;
  letter-spacing: 0.3px;
  text-align: center;

  @media screen and (width <= 359px) {
    padding: 0 1rem;
  }

  &:hover {
    box-shadow: 0 10px 20px rgb(65 82 92 / 20%);
  }

  &:active {
    box-shadow: none;
  }

  &:focus {
    box-shadow: 0 10px 20px rgb(65 82 92 / 20%);
  }

  &:disabled {
    box-shadow: none;
    cursor: not-allowed;
  }

  &[disabled] {
    box-shadow: none;
    cursor: not-allowed;
  }
}

.adn-button--primary {
  background-color: var(--color-main);
  color: white;

  &:hover {
    background-color: var(--color-main-dark);
    color: white;
  }

  &:active {
    background-color: var(--color-main);
    color: white;
  }

  &:focus {
    background-color: var(--color-main-dark);
    border-color: var(--color-main-dark);
    color: white;
  }

  &:disabled {
    background-color: var(--color-inactive-light);
    color: var(--color-text-light);
  }

  &[disabled] {
    background-color: var(--color-inactive-light);
    color: var(--color-text-light);
  }
}

.adn-button--secondary {
  background-color: var(--color-background);
  border: solid 1px var(--color-main);

  &:hover {
    border-color: var(--color-main-dark);
    box-shadow: 0 10px 20px rgb(65 82 92 / 20%);
    color: var(--color-main-dark);
  }

  &:active {
    color: var(--color-main);
    border-color: var(--color-main);
    box-shadow: none;
  }

  &:focus {
    background-color: var(--color-border);
    border-color: var(--color-main-dark);
    box-shadow: 0 10px 20px rgb(65 82 92 / 20%);
    color: var(--color-main-dark);
  }

  &:disabled {
    background-color: white;
    border-color: var(--color-inactive-light);
    box-shadow: none;
    color: var(--color-text-light);
    cursor: not-allowed;
  }

  &[disabled] {
    background-color: white;
    border-color: var(--color-inactive-light);
    box-shadow: none;
    color: var(--color-text-light);
    cursor: not-allowed;
  }
}

.adn-button--tertiary {
  background-color: var(--color-background-light);
  border-color: var(--color-border-light);
  color: var(--color-text);

  &:hover {
    background-color: var(--color-background);
    box-shadow: none;
  }

  &:focus {
    box-shadow: none;
  }

  &:active {
    box-shadow: 0.1rem 0.2rem 0.8rem 0 #33366052;
  }
}

.adn-button--light {
  background-color: inherit;
  border: none;
  color: var(--color-text);

  &:hover {
    background-color: var(--color-background);
    box-shadow: none;
  }

  &:active {
    box-shadow: 0.1rem 0.2rem 0.8rem 0 #33366052;
  }

  &:focus {
    box-shadow: none;
  }

  &[disabled] {
    border: none;
    box-shadow: none;
    color: var(--color-text-light);
  }

  &-white {
    color: var(--color-white);

    &:hover {
      background-color: color-mix(in srgb, var(--color-white) 20%, transparent 80%);
      box-shadow: none;
    }
  }
}

.adn-button--link {
  background-color: transparent;
  border-color: transparent;
  box-shadow: none;
  color: var(--color-main);

  &:hover {
    box-shadow: none;
    color: var(--color-main-dark);
  }

  &:active {
    background-color: transparent;
    border-color: transparent;
    color: var(--color-main);
  }

  &:focus {
    background-color: transparent;
    border-color: transparent;
    box-shadow: none;
  }

  .adn-button__text {
    display: inline-block;
    text-decoration: underline;
  }

  &:disabled,
  &[disabled] {
    border-color: transparent;
    color: var(--color-inactive-dark);
    text-decoration: none;

    .adn-button__text {
      text-decoration: none;
    }
  }
}

span + .adn-button__icon--right {
  margin-left: 0.5rem;
}

.adn-button__icon--left + span {
  margin-left: 0.5rem;
}
</style>
